/*
Copyright 2026 Chain Care.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { logger as rootLogger } from "matrix-js-sdk/lib/logger";

import type { CallViewModel } from "../state/CallViewModel/CallViewModel.ts";
import type { PrenigmaTtsPlaybackRegistrationRefs } from "./voiceControlTtsPlayback.ts";
import type {
  PrenigmaTtsPlayableMediaElement,
  StartPrenigmaTtsPlaybackSessionOptions,
  StartPrenigmaTtsPlaybackSessionResult,
} from "./voiceControlTtsPlayer.ts";

const logger = rootLogger.getChild("[PrenigmaTtsPlaybackBridge]");

export interface PrenigmaTtsPlaybackRequest {
  audioSource: string;
  correlationIds?: string[];
  muted?: boolean;
  obsoleteTurnId: string;
  preload?: string;
  refNamespace?: string;
  refs?: Partial<PrenigmaTtsPlaybackRegistrationRefs>;
  requestId: string;
}

export type PrenigmaTtsPlaybackAcknowledgementStatus =
  | StartPrenigmaTtsPlaybackSessionResult["status"]
  | "duplicate_ignored"
  | "invalid_request"
  | "starter_unavailable";

export interface PrenigmaTtsPlaybackAcknowledgement {
  audioSourceReturned: false;
  error?: string;
  playInvoked: boolean;
  playResolved: boolean;
  providerCredentialsReturned: false;
  rawAudioReturned: false;
  refs?: PrenigmaTtsPlaybackRegistrationRefs;
  registered: boolean;
  requestId: string;
  status: PrenigmaTtsPlaybackAcknowledgementStatus;
  transcriptReturned: false;
}

export interface PrenigmaTtsPlaybackRuntimeHost {
  acknowledgePlaybackRequest?: (
    acknowledgement: PrenigmaTtsPlaybackAcknowledgement,
  ) => void;
  onPlaybackRequest?: (
    handler: (request: PrenigmaTtsPlaybackRequest) => void,
  ) => (() => void) | void;
  readPendingPlaybackRequests?: () => PrenigmaTtsPlaybackRequest[];
}

export interface ConnectPrenigmaTtsPlaybackBridgeOptions {
  audioFactory?: () => PrenigmaTtsPlayableMediaElement;
  host?: PrenigmaTtsPlaybackRuntimeHost;
  onAcknowledgement?: (
    acknowledgement: PrenigmaTtsPlaybackAcknowledgement,
  ) => void;
  vm: Pick<CallViewModel, "startPrenigmaTtsPlaybackSession">;
}

export interface PrenigmaTtsPlaybackBridge {
  disconnect: () => void;
}

export function connectPrenigmaTtsPlaybackBridge({
  audioFactory,
  host,
  onAcknowledgement,
  vm,
}: ConnectPrenigmaTtsPlaybackBridgeOptions): PrenigmaTtsPlaybackBridge {
  if (host === undefined) {
    return { disconnect: (): void => {} };
  }

  let connected = true;
  const handledRequestIds = new Set<string>();
  const handleRequest = (request: PrenigmaTtsPlaybackRequest): void => {
    if (!connected) return;
    void handlePrenigmaTtsPlaybackRequest({
      audioFactory,
      connected: () => connected,
      handledRequestIds,
      host,
      onAcknowledgement,
      request,
      vm,
    });
  };

  for (const request of readPendingPlaybackRequests(host)) {
    handleRequest(request);
  }

  const unsubscribe = host.onPlaybackRequest?.(handleRequest);

  return {
    disconnect: (): void => {
      connected = false;
      unsubscribe?.();
    },
  };
}

async function handlePrenigmaTtsPlaybackRequest({
  audioFactory,
  connected,
  handledRequestIds,
  host,
  onAcknowledgement,
  request,
  vm,
}: {
  audioFactory?: () => PrenigmaTtsPlayableMediaElement;
  connected: () => boolean;
  handledRequestIds: Set<string>;
  host: PrenigmaTtsPlaybackRuntimeHost;
  onAcknowledgement?: (
    acknowledgement: PrenigmaTtsPlaybackAcknowledgement,
  ) => void;
  request: PrenigmaTtsPlaybackRequest;
  vm: Pick<CallViewModel, "startPrenigmaTtsPlaybackSession">;
}): Promise<void> {
  const validation = validatePlaybackRequest(request);
  if (!connected()) return;
  if (!validation.valid) {
    acknowledgePlaybackRequest(
      host,
      {
        ...baseAcknowledgement(validation.requestId),
        error: validation.error,
        status: "invalid_request",
      },
      onAcknowledgement,
    );
    return;
  }

  if (handledRequestIds.has(validation.request.requestId)) {
    acknowledgePlaybackRequest(
      host,
      {
        ...baseAcknowledgement(validation.request.requestId),
        error: "duplicate_request_id",
        status: "duplicate_ignored",
      },
      onAcknowledgement,
    );
    return;
  }
  handledRequestIds.add(validation.request.requestId);

  if (vm.startPrenigmaTtsPlaybackSession === undefined) {
    acknowledgePlaybackRequest(
      host,
      {
        ...baseAcknowledgement(validation.request.requestId),
        error: "tts_playback_starter_unavailable",
        status: "starter_unavailable",
      },
      onAcknowledgement,
    );
    return;
  }

  const options: Omit<StartPrenigmaTtsPlaybackSessionOptions, "controller"> = {
    audioSource: validation.request.audioSource,
    obsoleteTurnId: validation.request.obsoleteTurnId,
  };
  if (audioFactory !== undefined) options.audioFactory = audioFactory;
  if (validation.request.correlationIds !== undefined) {
    options.correlationIds = validation.request.correlationIds;
  }
  if (validation.request.muted !== undefined) {
    options.muted = validation.request.muted;
  }
  if (validation.request.preload !== undefined) {
    options.preload = validation.request.preload;
  }
  if (validation.request.refNamespace !== undefined) {
    options.refNamespace = validation.request.refNamespace;
  }
  if (validation.request.refs !== undefined) {
    options.refs = validation.request.refs;
  }

  const result = await vm.startPrenigmaTtsPlaybackSession(options);
  if (!connected()) return;

  acknowledgePlaybackRequest(
    host,
    acknowledgementFromSessionResult({
      requestId: validation.request.requestId,
      result,
    }),
    onAcknowledgement,
  );
}

function readPendingPlaybackRequests(
  host: PrenigmaTtsPlaybackRuntimeHost,
): PrenigmaTtsPlaybackRequest[] {
  try {
    return host.readPendingPlaybackRequests?.() ?? [];
  } catch (error) {
    logger.warn("Prenigma TTS playback pending-request read failed", {
      error,
    });
    return [];
  }
}

function acknowledgePlaybackRequest(
  host: PrenigmaTtsPlaybackRuntimeHost,
  acknowledgement: PrenigmaTtsPlaybackAcknowledgement,
  onAcknowledgement?: (
    acknowledgement: PrenigmaTtsPlaybackAcknowledgement,
  ) => void,
): void {
  try {
    host.acknowledgePlaybackRequest?.(acknowledgement);
    onAcknowledgement?.(acknowledgement);
  } catch (error) {
    logger.warn("Prenigma TTS playback acknowledgement failed", {
      error,
      requestId: acknowledgement.requestId,
      status: acknowledgement.status,
    });
  }
}

function acknowledgementFromSessionResult({
  requestId,
  result,
}: {
  requestId: string;
  result: StartPrenigmaTtsPlaybackSessionResult;
}): PrenigmaTtsPlaybackAcknowledgement {
  if (result.status === "playing") {
    return {
      ...baseAcknowledgement(requestId),
      playInvoked: result.playInvoked,
      playResolved: result.playResolved,
      refs: result.refs,
      registered: result.registered,
      status: result.status,
    };
  }
  if (result.status === "play_failed") {
    return {
      ...baseAcknowledgement(requestId),
      error: safeDiagnostic(result.error),
      playInvoked: result.playInvoked,
      playResolved: result.playResolved,
      refs: result.refs,
      registered: result.registered,
      status: result.status,
    };
  }
  return {
    ...baseAcknowledgement(requestId),
    playInvoked: result.playInvoked,
    playResolved: result.playResolved,
    registered: result.registered,
    status: result.status,
  };
}

function baseAcknowledgement(
  requestId: string,
): Omit<PrenigmaTtsPlaybackAcknowledgement, "status"> {
  return {
    audioSourceReturned: false,
    playInvoked: false,
    playResolved: false,
    providerCredentialsReturned: false,
    rawAudioReturned: false,
    registered: false,
    requestId,
    transcriptReturned: false,
  };
}

function validatePlaybackRequest(
  request: PrenigmaTtsPlaybackRequest,
):
  | { request: PrenigmaTtsPlaybackRequest; valid: true }
  | { error: string; requestId: string; valid: false } {
  const requestId = safeText(request?.requestId);
  if (requestId === undefined) {
    return {
      error: "request_id_required",
      requestId: "unknown",
      valid: false,
    };
  }

  const audioSource = safeText(request.audioSource);
  if (audioSource === undefined) {
    return {
      error: "audio_source_required",
      requestId,
      valid: false,
    };
  }
  if (isInlineRawAudioSource(audioSource)) {
    return {
      error: "audio_source_must_not_inline_raw_audio",
      requestId,
      valid: false,
    };
  }
  if (!isPlayableAudioSource(audioSource)) {
    return {
      error: "audio_source_scheme_not_allowed",
      requestId,
      valid: false,
    };
  }

  const obsoleteTurnId = safeText(request.obsoleteTurnId);
  if (obsoleteTurnId === undefined) {
    return {
      error: "obsolete_turn_id_required",
      requestId,
      valid: false,
    };
  }

  return {
    request: {
      ...request,
      audioSource,
      correlationIds: request.correlationIds
        ?.map((correlationId) => correlationId.trim())
        .filter((correlationId) => correlationId !== ""),
      obsoleteTurnId,
      requestId,
    },
    valid: true,
  };
}

function safeText(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function isInlineRawAudioSource(audioSource: string): boolean {
  return /^data:/i.test(audioSource);
}

function isPlayableAudioSource(audioSource: string): boolean {
  if (/^(blob|https?):/i.test(audioSource)) return true;
  if (audioSource.startsWith("/")) return !audioSource.startsWith("//");
  return false;
}

function safeDiagnostic(error: string): string {
  if (
    /data:audio|audio_bytes|raw_audio|provider_api_key|password|bearer/i.test(
      error,
    )
  ) {
    return "redacted_playback_error";
  }
  return error;
}
